import { useState } from 'react';
import { Button } from './Button';
import { useOnline } from './useConnection';
import {
  failedWriteLog,
  labelFor,
  useFailedWriteList,
  type FailedWriteView,
} from '../../lib/failed-writes';

/**
 * Work the server refused, and the two things a tech can do about it.
 *
 * ⭐ **Above every tech screen, not on the one that made the write.** A payment refused on a
 * stop the tech left an hour ago is invisible from that stop — they are three jobs away by
 * the time the queue replays. The photo refusal bar can point at a job because a photo
 * belongs to one; a refused payment, a checklist tick and a finished job do not share a
 * destination, so the destination is here.
 *
 * The panel is deliberately not dismissible as a whole. Each row leaves because the server
 * took it or because a person decided what to do about it, and a "hide this" would put the
 * silence straight back.
 */
export function UnsentWorkPanel() {
  const rows = useFailedWriteList();
  if (rows.length === 0) return null;

  return (
    <section
      aria-labelledby="unsent-work-heading"
      className="border-b border-border bg-surface-2 px-4 py-3"
    >
      {/*
       * ⭐ **Not "N things didn't send", and the repo's own guard is what caught it.**
       * `delivery-copy.test.ts` bans that claim because it is a GUESS whenever the request
       * left and no answer came back — and that is precisely this panel's transient bucket
       * (`refusalStatus` 0 is a dead connection). A tech told their payment did not send,
       * for a write that in fact landed, goes and collects it again. Each row says what is
       * actually known about ITSELF below; the heading only claims what is true of all of
       * them, which is that they are here and they need a person.
       */}
      <h2 id="unsent-work-heading" className="font-heading text-sm font-bold">
        {rows.length === 1 ? 'One thing needs you' : `${rows.length} things need you`}
      </h2>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {/* The promise 016 could not make, now that it is true: these are on the phone. */}
        Still on this phone. Nothing is lost — each one needs you to decide.
      </p>
      <ul className="mt-3 space-y-3">
        {rows.map((row) => (
          <Row key={row.mutationId} row={row} />
        ))}
      </ul>
    </section>
  );
}

function Row({ row }: { row: FailedWriteView }) {
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const act = (run: () => Promise<void>) => () => {
    setBusy(true);
    void run().finally(() => setBusy(false));
  };

  return (
    <li className="rounded-[var(--radius)] border border-border bg-surface p-3">
      <p className="text-sm font-semibold">{labelFor(row.path)}</p>
      {/* The server's own words. A paraphrase is where an invented reason gets in, and this
          is the sentence the tech has to act on. */}
      <p className="mt-1 text-xs text-muted-foreground">{row.reason}</p>
      {/*
       * The panel's heading promises everything here is "still on this phone". For a row the
       * store refused — a full phone, a private-mode origin — that promise ends when the app
       * is closed, so this row says the different thing rather than inheriting the general
       * one. Silence about it would be the 016 defect; the general promise would be the lie
       * 016 removed.
       */}
      {/*
       * Adoption is a GUESS. This row was refused with nobody signed in — restored mutations
       * replay before the session query answers — and the account that signed in next is only
       * probably its author. On a shop tablet it may be a different technician, so the label
       * above ("A payment you took") would be a false claim, and this corrects it rather than
       * hiding the row: a payment nobody is ever shown is the defect this panel exists for.
       */}
      {row.adopted ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Recorded on this phone before anyone was signed in — it may not be yours.
        </p>
      ) : null}
      {!row.durable ? (
        <p className="mt-1 text-xs font-semibold text-danger">
          This phone is out of room, so this one is only held while the app is open. Deal
          with it now.
        </p>
      ) : null}

      {row.retryable ? (
        <>
          {/*
           * What is actually known, which is less than it looks. A transient failure covers
           * a dead connection — the request may have reached the server and only the answer
           * was lost. Saying so is the honest half; the second sentence is why that costs
           * nothing here, and it is a real guarantee rather than reassurance: `retry()`
           * replays the ORIGINAL `clientId`/`mutationId`, and the server dedupes on the pair
           * (Foundation Rule 3). A write that already landed applies exactly once.
           */}
          <p className="mt-1 text-xs text-muted-foreground">
            Trying again is safe: Servwell retries the original request, so it can’t be
            recorded twice.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              variant="primary"
              disabled={busy || row.sending || !online}
              onClick={act(() => failedWriteLog.retry(row.mutationId))}
            >
              {row.sending || busy ? 'Sending…' : 'Try again'}
            </Button>
            {/* Not a disabled button with no explanation: offline, the reason it cannot be
                tapped is the whole context, and it is the one thing that will change. */}
            {!online ? (
              <span className="text-xs text-muted-foreground">Waiting for signal.</span>
            ) : null}
          </div>
          {/*
           * 🔴 **A way out on this arm too, or the tech is stranded.** With no signal the
           * retry cannot fire, and `useFailedWriteFor` shuts the payment control this row
           * belongs to — so without a clear action a technician standing in a basement can
           * neither send this payment nor take it again nor put the row down. The exit is
           * deliberately quieter than "Try again": trying is the right answer whenever there
           * IS a signal, and this is only for when there is not, or when a tech has settled
           * it another way.
           */}
          {confirming ? (
            <div className="mt-2 flex items-center gap-2">
              <Button
                variant="danger"
                disabled={busy}
                onClick={act(() => failedWriteLog.discard(row.mutationId))}
              >
                Yes, clear it
              </Button>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => setConfirming(false)}
              >
                Keep it
              </Button>
            </div>
          ) : (
            <button
              type="button"
              className="mt-2 text-xs text-muted-foreground underline underline-offset-2"
              onClick={() => setConfirming(true)}
            >
              I’ve dealt with this another way
            </button>
          )}
        </>
      ) : (
        <div className="mt-3">
          {/*
           * No "Try again" here on purpose. This refusal will not go differently, and a
           * button that can only fail invites the one action guaranteed not to work — the
           * same rule `ForbiddenState` follows for a 403 page.
           */}
          <p className="text-xs font-semibold text-danger">
            Servwell can’t record this one. Write it down before you clear it.
          </p>
          {confirming ? (
            <div className="mt-2 flex items-center gap-2">
              <Button
                variant="danger"
                disabled={busy}
                onClick={act(() => failedWriteLog.discard(row.mutationId))}
              >
                Yes, clear it
              </Button>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => setConfirming(false)}
              >
                Keep it
              </Button>
            </div>
          ) : (
            // Two taps, because the first one throws away the only record that this
            // happened. The wording is what the tech is actually asserting.
            <Button variant="ghost" className="mt-2" onClick={() => setConfirming(true)}>
              I’ve dealt with this
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
